import { Sale } from 'features/common/hooks/useHighlights/types';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const MINUTES_PER_MONTH = 30 * MINUTES_PER_DAY;

/**
 * Formats a lead-time duration (in minutes) into a human-readable string using
 * the most appropriate scale:
 *
 *   ≥ 30 days  →  "Xm Yd"   (months + days)
 *   ≥  1 day   →  "Xd [Yh]" (days; hours shown only when non-zero)
 *   ≥  1 hour  →  "Xh [Ym]" (hours; minutes shown only when non-zero)
 *   <  1 hour  →  "Xm"      (minutes only)
 *   ≤  0       →  "<1h"     (pre-show sales with no measurable lead time)
 *
 * With daily-bucketed sales data the `minutes` value will always be a multiple
 * of MINUTES_PER_DAY (1440), so the sub-day branches are dormant until per-order
 * or hourly data becomes available from the API. The function handles that future
 * data transparently — no code change required when the data source improves.
 */
export function formatLeadTime(minutes: number): string {
	if (minutes <= 0) return '<1h';

	if (minutes >= MINUTES_PER_MONTH) {
		const months = Math.floor(minutes / MINUTES_PER_MONTH);
		const days = Math.floor((minutes % MINUTES_PER_MONTH) / MINUTES_PER_DAY);
		return days > 0 ? `${months}m ${days}d` : `${months}m`;
	}

	if (minutes >= MINUTES_PER_DAY) {
		const days = Math.floor(minutes / MINUTES_PER_DAY);
		const hours = Math.floor((minutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
		return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
	}

	const hours = Math.floor(minutes / MINUTES_PER_HOUR);
	const mins = Math.round(minutes % MINUTES_PER_HOUR);
	if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
	return `${mins}m`;
}

/**
 * Computes the weighted-average lead time (in minutes) from daily-bucketed sales.
 *
 * Formula: Σ(leadMinutes_i × soldOnDay_i) / Σ(soldOnDay_i)
 *
 * Lead time per bucket = max(0, showStartMs − saleDateMs) / 60_000.
 * Clamped to 0 so post-show corrections (negative values) don't skew the result
 * downward — a correction made one day after the show is treated as "same day"
 * rather than "-1 day".
 *
 * Returns null when there is no sales data or the event start date is missing.
 */
export function computeAvgLeadTimeMinutes(sales: Sale<string>[], startDate: string | undefined): number | null {
	if (!startDate || !sales.length) return null;
	const showMs = new Date(startDate).getTime();
	let totalSold = 0;
	let weightedMinutes = 0;
	for (const sale of sales) {
		const soldOnDay = sale.ticket_types.reduce((sum, tt) => sum + tt.sold, 0);
		if (soldOnDay <= 0) continue;
		const leadMinutes = Math.max(0, Math.round((showMs - new Date(sale.date).getTime()) / 60_000));
		weightedMinutes += leadMinutes * soldOnDay;
		totalSold += soldOnDay;
	}
	if (totalSold === 0) return null;
	return Math.round(weightedMinutes / totalSold);
}

/**
 * Formats a ratio (0–1) as a percentage string with up to 2 decimal places.
 * Trailing zeros are stripped: 0.70 → "70%", 0.703 → "70.3%", 0.7034 → "70.34%".
 *
 * "Up to 2 decimal places" rather than always 2 prevents KPI tiles from
 * displaying noise digits (e.g. "70.00%" instead of "70%") while still
 * preserving meaningful precision at low percentages where rounding to the
 * nearest whole number would be misleading.
 */
export function formatPct(ratio: number): string {
	return `${parseFloat((ratio * 100).toFixed(2))}%`;
}

/** Minimal per-type shape for the revenue-weighted comp rate (subset of `CapacityType`). */
export interface CompRateType {
	sold: number; // issued count (snapshot includes comps)
	compedCount: number; // comped tickets (clamped <= sold)
	initialPrice: number;
	maxPrice: number; // PWYW ceiling price; 0 when not PWYW
	payWhatYouWant: boolean;
}

/**
 * Revenue-weighted comp rate — the share of issued-ticket VALUE given away as
 * comps rather than sold: `Σ(comped × price) ÷ Σ(issued × price)`, where price
 * is each type's effective price (maxPrice for PWYW, initialPrice otherwise;
 * free types contribute 0 to both sides). Weighting by price means a comped VIP
 * counts more than a comped GA. The denominator is issued value (paid + comped)
 * — "of the value of every ticket put out, what share went out free."
 *
 * All inputs are tixologist-owned, so this KPI is reliable in every context
 * (unlike the GA4-sourced page-view conversion rate it replaces). Returns null
 * when there is no priced issued value (avoids ÷0) so the tile renders '—'.
 */
export function computeRevenueCompRate(types: CompRateType[]): number | null {
	let issuedRev = 0;
	let compedRev = 0;
	for (const t of types) {
		const price = t.payWhatYouWant ? t.maxPrice : t.initialPrice;
		if (price <= 0) continue;
		issuedRev += t.sold * price;
		compedRev += t.compedCount * price;
	}
	return issuedRev > 0 ? compedRev / issuedRev : null;
}
